/**
 * Pure helpers for the read-only transcript page (README §8; v2.3 §10-11).
 * No component code, no store/ops access — mirrors the split already
 * established by `src/components/thread/format.ts` and
 * `src/components/rail/format.ts`.
 */
import type { ThreadState } from '@communicator/shared'
import { formatOpenedAtInZone } from '@communicator/shared'

/**
 * v2.3 §10: "Close is terminal." A thread is only ever readable here once it
 * has actually closed — every other state (incoming/active/scheduled) is
 * still writable and belongs on the live pane instead.
 */
export function isThreadClosed(state: ThreadState): boolean {
  return state === 'closed'
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * Renders an ISO timestamp as a stable, timezone-independent string (UTC,
 * spelled out) so the "opened at" fact in the READ ONLY banner doesn't drift
 * with the reader's local clock or flake in CI across timezones. Falls back
 * to the raw string for anything that doesn't parse, rather than showing
 * "Invalid Date".
 */
export function formatOpenedAt(iso: string, timeZone: string): string {
  return formatOpenedAtInZone(iso, timeZone)
}

/** "READ ONLY · CLOSE IS TERMINAL" names who had it open and when — never a placeholder owner. */
export function openedByLine(owner: string | null, openedAt: string, timeZone: string): string {
  const who = owner ?? 'nobody (never claimed)'
  return `Opened by ${who} · ${formatOpenedAt(openedAt, timeZone)}. Closed threads never reopen — a returning client starts a fresh one.`
}
